import React from 'react'
import { graphql } from 'react-apollo'

import { Spinner } from '@acorns/web-components'
import { pathOr } from 'ramda'
import { branch, compose, renderComponent, withHandlers, withProps, withState } from 'recompose'

import SpendCardPNG from 'assets/images/spend-card.png'
import Button from 'components/Button'
import CenteredSpinner from 'components/CenteredSpinner'
import withHeroText, { HeroTextProps } from 'components/HOCs/withHeroText'
import Layout from 'components/Layout'
import Nav from 'components/Nav'
import Separator from 'components/Separator'
import { withClickTrack } from 'components/Trackers/ClickTracker'
import { TRACKER_IDS } from 'components/Trackers/trackerIDs'
import { TestIDs } from 'test/utils/testIds'
import { sendToSentry } from 'utils/errors'
import { WithFlowRouterProps, withFlowRouter } from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'

import orderBankCardMutation from './gql/orderBankCard.mutation.gql'
import orderConfirmationQuery from './gql/orderConfirmation.query.gql'
import {
  Address,
  AddressContainer,
  Container,
  CustomerName,
  CustomerSignature,
  CustomerSignatureContainer,
  EditAddress,
  EditSignature,
  MailTo,
  MailingAddressContainer,
  SpendCard,
  SpendCardContainer,
  Subtitle,
  Title,
} from './styled-components'

type Props = {
  signatureSVG?: string | null
  displayName?: string
  fullName?: string
  mailingAddress?: Address
  handleEditSignature?: () => void
  handleEditAddress?: () => void
  handleNext?: () => void
  isSubmitting: boolean
  setIsSubmitting: (isSubmitting: boolean) => void
} & HeroTextProps &
  GqlProps &
  WithFlowRouterProps

type GqlProps = {
  user: any
  signatures: any
  addresses: any
}

type Address = {
  street1?: string
  street2?: string
  city?: string
  state?: string
  zip?: string
}

const NextButton = withClickTrack<any, any>(TRACKER_IDS.SPEND_ORDER_CONFIRMATION.NEXT_BUTTON)(
  Button,
)

const enhance = compose<Props, {}>(
  withHeroText,
  withFlowRouter,
  graphql(orderConfirmationQuery, {
    options: { fetchPolicy: 'network-only' },
    props: ({
      data: { signatures, user, addresses, spendPrefundAmount, bankCardOrder, loading },
    }) => {
      if (!loading) {
        const signatureSVG = pathOr(null, [0, 'svgFile'], signatures)

        const firstName = pathOr(null, ['firstName'], user)
        const lastName = pathOr(null, ['lastName'], user)

        let totalChars = firstName ? firstName.length : 0
        totalChars += lastName ? lastName.length : 0
        totalChars += 1 // space character

        // Long names will be displayed on two lines
        const displayName =
          totalChars < 32
            ? `${firstName} ${lastName}`
            : `${firstName}
          ${lastName}`

        return {
          signatureSVG,
          loading,
          bankCardOrder,
          displayName,
          fullName: `${firstName} ${lastName}`,
          prefundAmount: spendPrefundAmount ? spendPrefundAmount.value : null,
          mailingAddress: {
            street1: pathOr(null, [0, 'street1'], addresses),
            street2: pathOr(null, [0, 'street2'], addresses),
            city: pathOr(null, [0, 'city'], addresses),
            state: pathOr(null, [0, 'state'], addresses),
            zip: pathOr(null, [0, 'zipCode'], addresses),
          },
        }
      } else {
        return {
          loading,
        }
      }
    },
  }),
  graphql(orderBankCardMutation, {
    props: ({ mutate }) => ({
      orderBankCard: mutate,
    }),
  }),
  withProps(({ route, bankCardOrder }) => {
    const cardOrderAlreadyExists = bankCardOrder && bankCardOrder.cardOrderStatus !== 'notOrdered'
    if (cardOrderAlreadyExists) {
      route({ from: FlowRoute.SpendOrderConfirmation, action: 'skip' })
    }
  }),
  withState('isSubmitting', 'setIsSubmitting', false),
  withHandlers({
    handleEditSignature: ({ route }) => () => {
      route({ from: FlowRoute.SpendOrderConfirmation, action: 'edit-signature' })
    },
    handleEditAddress: ({ route }) => () => {
      route({ from: FlowRoute.SpendOrderConfirmation, action: 'edit-address' })
    },
    handleNext: ({ route, orderBankCard, setIsSubmitting, prefundAmount }) => () => {
      setIsSubmitting(true)
      orderBankCard({
        variables: { amount: prefundAmount },
      })
        .then(() => {
          route({ from: FlowRoute.SpendOrderConfirmation, action: 'continue' })
        })
        .catch(error => {
          sendToSentry(error)
          setIsSubmitting(false)
        })
    },
  }),
  branch(({ loading }) => loading, renderComponent(CenteredSpinner)),
)

const SpendOrderConfirmation: React.SFC<Props> = ({
  heroText,
  handleEditSignature,
  handleEditAddress,
  signatureSVG,
  displayName,
  fullName,
  mailingAddress,
  handleNext,
  isSubmitting,
}) => (
  <>
    <Nav />
    <Layout>
      <Container>
        <Title>{heroText.header}</Title>
        <Subtitle>{heroText.text}</Subtitle>
        <SpendCardContainer>
          <SpendCard
            src={SpendCardPNG}
            data-testid={TestIDs.SPEND_ORDER_CONFIRMATION.SPEND_CARD_IMAGE}
          />
          <CustomerSignatureContainer>
            <CustomerSignature
              src={`data:image/svg+xml;utf8,${signatureSVG}`}
              data-testid={TestIDs.SPEND_ORDER_CONFIRMATION.SPEND_CARD_USER_SIGNATURE}
            />
          </CustomerSignatureContainer>
          <CustomerName
            displayName={displayName}
            data-testid={TestIDs.SPEND_ORDER_CONFIRMATION.SPEND_CARD_USER_NAME}
          >
            {displayName}
          </CustomerName>
        </SpendCardContainer>
        <EditSignature
          onClick={handleEditSignature}
          data-testid={TestIDs.SPEND_ORDER_CONFIRMATION.EDIT_SIGNATURE_BUTTON}
        >
          Edit signature
        </EditSignature>
        <MailingAddressContainer>
          <AddressContainer>
            <MailTo>{'We’ll mail it to:'}</MailTo>
            {fullName && <Address displayName={displayName}>{`${fullName}`}</Address>}
            {mailingAddress.street1 && <Address>{`${mailingAddress.street1}`}</Address>}
            {mailingAddress.street2 && <Address>{`${mailingAddress.street2}`}</Address>}
            {mailingAddress.city && mailingAddress.state && mailingAddress.zip && (
              <Address>{`${mailingAddress.city}, ${mailingAddress.state} ${mailingAddress.zip}`}</Address>
            )}
          </AddressContainer>
          <EditAddress
            onClick={handleEditAddress}
            data-testid={TestIDs.SPEND_ORDER_CONFIRMATION.EDIT_ADDRESS_BUTTON}
          >
            Edit address
          </EditAddress>
        </MailingAddressContainer>
        <Separator />
        <NextButton
          onPress={handleNext}
          disabled={isSubmitting}
          data-testid={TestIDs.SPEND_ORDER_CONFIRMATION.NEXT_BUTTON}
        >
          {isSubmitting ? <Spinner height={20} width={20} white /> : 'Next'}
        </NextButton>
      </Container>
    </Layout>
  </>
)

export default enhance(SpendOrderConfirmation)
